import { css } from '@styled-system/css';
import { token } from '@styled-system/tokens';
import type { SystemProperties } from '@styled-system/types';
import { type ReactNode, useState } from 'react';
import { PuffLoader } from 'react-spinners';

import { Logo, type LogoVariant } from './Logo';

interface ButtonProps {
  children: ReactNode;
  handleClick?: () => void | Promise<void>;
  disabled?: boolean;
  icon?: LogoVariant;
  className?: string;
  color?: SystemProperties['color'];
  showLoader?: boolean;
}

export const Button = ({
  children,
  handleClick,
  disabled: disabledProp,
  icon,
  className,
  color,
  showLoader = false,
}: ButtonProps) => {
  const [isLoading, setLoading] = useState(false);

  const disabled = (disabledProp ?? false) || isLoading;

  const handleClickEvent = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    (async () => {
      setLoading(true);
      try {
        await handleClick?.();
      } finally {
        setLoading(false);
      }
    })().catch(console.error);
  };

  // Résoudre la couleur du token Panda CSS en valeur CSS réelle
  const resolvedColor = color
    ? typeof color === 'string' && color.includes('.')
      ? token.var(`colors.${color}` as any)
      : color
    : undefined;

  const buttonStyles = css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: icon ? 'xxs' : 0, // spacing-xxs (2px) gap when icon is present
    p: '0.5rem',

    // Background and border from Figma
    bg: 'base.segBg', // #16171b (default state)
    border: '0.5px solid',
    borderColor: 'base.border', // #1e1f24
    rounded: 'sm', // radii-ms (6px) border radius

    // Interaction states
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    transition: 'background-color 150ms ease, opacity 150ms ease',

    // Hover state from Figma
    _hover: disabled
      ? {}
      : {
          bg: 'base.selectedBg', // #222328 (hover state)
        },

    // Active state - simulate button press with opacity
    _active: disabled
      ? {}
      : {
          bg: 'base.selectedBg', // #222328 (same as hover)
          opacity: 0.8, // Simulate press effect
        },

    // Remove focus outline
    _focus: {
      outline: 'none',
    },
  });

  const textStyles = css({
    // Typography
    fontSize: '2xs', // font-2xs (9px)
    fontWeight: 'semibold', // Manrope SemiBold
    fontFamily: 'Manrope, sans-serif',
    color: color ?? 'font.primary', // #e6e6e9 par défaut
    textAlign: 'center',
    whiteSpace: 'nowrap',
    lineHeight: '1',
  });

  return (
    <button
      className={`${buttonStyles} ${className || ''}`}
      onClick={handleClickEvent}
      disabled={disabled}
      type="button"
    >
      <div className={css({ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '0.02rem' })}>
        {icon && <Logo variant={icon} color={resolvedColor} />}
      </div>
      <div className={textStyles}>{children}</div>
      {isLoading && showLoader && (
        <PuffLoader
          size={10}
          color={token.var('colors.font.primary')}
          className={css({
            ml: 'xs',
          })}
        />
      )}
    </button>
  );
};
